import React, { useEffect, useMemo, useState } from 'react';
import { S, Field, Select, SearchSelect, itemOptions, fmtQty, fmtMoney, fmtDate, today } from './StoresShared';
import { DocView, statusPill, useList } from './StoresPurchase';

// Stores module — Phase 2c: Purchase Requests, the Approvals inbox and the
// Approval rules editor (Settings). API: backend/v1/routes/storesRequests.js.

const n = (v) => Number(v) || 0;
const r2 = (v) => Math.round(n(v) * 100) / 100;
const key = () => Math.random().toString(36).slice(2);
const small = { fontSize: '0.72rem', color: '#94a3b8' };
const can = (boot, ...roles) => boot.me.is_hotel_admin || roles.some((r) => boot.me.roles.includes(r));
const roleText = (r) => (r === 'HOTEL_ADMIN' ? 'Hotel Admin' : r.replace(/_/g, ' ').toLowerCase().replace(/^\w/, (c) => c.toUpperCase()));
const unitsOf = (it) => (it ? [it.base_uom, ...(it.uoms || []).map((u) => u.uom_code)] : []);
const factorOf = (it, uom) => {
  if (!it || !uom || uom === it.base_uom) return 1;
  const u = (it.uoms || []).find((x) => x.uom_code === uom);
  return u ? n(u.factor_to_base) : 1;
};
const buyUnit = (it) => (it?.uoms || []).find((u) => u.is_purchase_uom)?.uom_code || it?.base_uom || '';
const blankLine = () => ({ key: key(), item_id: '', uom_code: '', qty: '', rate: '', remarks: '' });

const openWithApproval = (api, id) => Promise.all([api(`/docs/${id}`), api(`/approvals/doc/${id}`).catch(() => null)])
  .then(([d, approval]) => ({ ...d, approval }));

// ── Purchase requests ────────────────────────────────────────────────────
export function PurchaseRequests({ api, boot }) {
  const [items, setItems] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [status, setStatus] = useState('SUBMITTED,APPROVED');
  const { rows, err: listErr, loading, load } = useList(api, `/docs?doc_type=PR${status ? `&status=${status}` : ''}`);
  const [form, setForm] = useState(null);
  const [view, setView] = useState(null);
  const [lineVendors, setLineVendors] = useState({});
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);
  const canOrder = can(boot, 'PURCHASE_STAFF', 'PURCHASE_MANAGER', 'STORE_MANAGER');
  const stores = boot.stores.filter((s) => s.is_active);
  const store = form && boot.stores.find((s) => s.id === form.store_id);
  const byId = useMemo(() => Object.fromEntries(items.map((i) => [i.id, i])), [items]);
  const storeItems = store ? items.filter((i) => i.department_id === store.department_id) : [];

  useEffect(() => { api('/items?active=1').then(setItems).catch((e) => setErr(e.message)); }, [api]);
  useEffect(() => { if (canOrder) api('/vendors?active=1').then(setVendors).catch(() => setVendors([])); }, [api, canOrder]);

  const setLine = (k, patch) => setForm((f) => ({ ...f, lines: f.lines.map((l) => (l.key === k ? { ...l, ...patch } : l)) }));
  const pickItem = (l, id) => {
    const it = byId[id];
    const uom = buyUnit(it);
    setLine(l.key, { item_id: id, uom_code: uom, rate: it && n(it.standard_cost) ? String(r2(n(it.standard_cost) * factorOf(it, uom))) : '' });
  };
  const suggest = async () => {
    setErr('');
    try {
      const sug = await api(`/pr/suggest?store_id=${form.store_id}`);
      if (!sug.length) { setMsg('Nothing in this store is at or below its reorder level.'); return; }
      const have = new Set(form.lines.map((l) => l.item_id));
      const add = sug.filter((x) => !have.has(x.item_id)).map((x) => ({
        ...blankLine(), item_id: x.item_id, uom_code: x.base_uom, qty: String(n(x.suggest_qty)), rate: n(x.standard_cost) ? String(n(x.standard_cost)) : '',
        remarks: `In stock ${fmtQty(x.on_hand)}, reorder at ${fmtQty(x.reorder_qty)}`,
      }));
      setForm((f) => ({ ...f, lines: [...f.lines.filter((l) => l.item_id), ...add] }));
      setMsg(`${add.length} item(s) added from reorder levels.`);
    } catch (e) { setErr(e.message); }
  };
  const submit = async () => {
    setBusy(true); setErr('');
    try {
      const d = await api('/pr', { method: 'POST', body: { ...form, lines: form.lines.filter((l) => l.item_id && n(l.qty) > 0) } });
      setMsg(`${d.doc_no} sent for approval — estimated ${fmtMoney(d.grand_total)}.`); setForm(null); load();
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };
  const open = (id) => openWithApproval(api, id).then((d) => { setErr(''); setLineVendors({}); setView(d); }).catch((e) => setErr(e.message));
  const act = async (path, body, done) => {
    setBusy(true); setErr('');
    try { const r = await api(path, { method: 'POST', body }); setMsg(done(r)); setView(null); load(); } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };
  const total = form ? form.lines.reduce((s, l) => s + r2(n(l.qty) * n(l.rate)), 0) : 0;

  return (
    <>
      {(err || listErr) && !view && <div style={S.err}>{err || listErr}</div>}
      {msg && <div style={S.ok}>{msg}</div>}
      {!form && (
        <div style={S.bar}>
          <button type="button" className="btn-primary" disabled={!stores.length} onClick={() => { setErr(''); setMsg(''); setForm({ store_id: stores[0]?.id || '', doc_date: today(), expected_date: '', party_name: '', remarks: '', lines: [blankLine()] }); }}>+ New purchase request</button>
          <Field label="Show">
            <Select style={S.inp} value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="SUBMITTED,APPROVED">Open</option>
              <option value="SUBMITTED">Waiting for approval</option>
              <option value="APPROVED">Approved — to order</option>
              <option value="ORDERED">Ordered</option>
              <option value="REJECTED,CANCELLED">Rejected / cancelled</option>
              <option value="">All</option>
            </Select>
          </Field>
        </div>
      )}
      {form && (
        <div style={S.card}>
          <h3 style={{ margin: '0 0 0.75rem', fontSize: '1rem', color: '#1e3a5f' }}>New purchase request</h3>
          <div style={S.bar}>
            <Field label="For store">
              <Select style={{ ...S.inp, minWidth: 190 }} value={form.store_id} onChange={(e) => setForm({ ...form, store_id: e.target.value, lines: [blankLine()] })}>
                {stores.map((s) => <option key={s.id} value={s.id}>{s.store_name}</option>)}
              </Select>
            </Field>
            <Field label="Needed by"><input type="date" style={S.inp} value={form.expected_date} onChange={(e) => setForm({ ...form, expected_date: e.target.value })} /></Field>
            <Field label="Requested by"><input style={{ ...S.inp, width: 160 }} value={form.party_name} placeholder="Your name" onChange={(e) => setForm({ ...form, party_name: e.target.value })} /></Field>
            <button type="button" className="btn-muted" onClick={suggest} disabled={!form.store_id}>Add items below reorder level</button>
          </div>
          <div style={S.tableWrap}>
            <table style={S.table}>
              <thead><tr>
                <th style={S.th}>Item</th><th style={S.th}>Unit</th><th style={{ ...S.th, ...S.num }}>Qty</th><th style={{ ...S.th, ...S.num }}>Est. rate</th>
                <th style={{ ...S.th, ...S.num }}>Amount</th><th style={S.th}>Note</th><th style={S.th} />
              </tr></thead>
              <tbody>
                {form.lines.map((l) => {
                  const it = byId[l.item_id];
                  return (
                    <tr key={l.key}>
                      <td style={S.td}>
                        <SearchSelect style={{ minWidth: 230 }} value={l.item_id} options={itemOptions(storeItems)} onChange={(v) => pickItem(l, v)} />
                      </td>
                      <td style={S.td}>
                        <Select style={{ ...S.inp, width: 85 }} value={l.uom_code} disabled={!it} onChange={(e) => setLine(l.key, { uom_code: e.target.value })}>
                          {unitsOf(it).map((u) => <option key={u} value={u}>{u}</option>)}
                        </Select>
                      </td>
                      <td style={S.td}><input type="number" min="0" step="any" style={{ ...S.inp, width: 90, textAlign: 'right' }} value={l.qty} onChange={(e) => setLine(l.key, { qty: e.target.value })} /></td>
                      <td style={S.td}><input type="number" min="0" step="any" style={{ ...S.inp, width: 100, textAlign: 'right' }} value={l.rate} onChange={(e) => setLine(l.key, { rate: e.target.value })} /></td>
                      <td style={{ ...S.td, ...S.num }}>{fmtMoney(r2(n(l.qty) * n(l.rate)))}</td>
                      <td style={S.td}><input style={{ ...S.inp, width: 190 }} value={l.remarks} onChange={(e) => setLine(l.key, { remarks: e.target.value })} /></td>
                      <td style={S.td}><button type="button" className="btn-muted btn-sm" aria-label="Remove line" onClick={() => setForm((f) => ({ ...f, lines: f.lines.length > 1 ? f.lines.filter((x) => x.key !== l.key) : [blankLine()] }))}>✕</button></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <button type="button" className="btn-muted btn-sm" style={{ marginTop: '0.6rem' }} onClick={() => setForm((f) => ({ ...f, lines: [...f.lines, blankLine()] }))}>+ Add line</button>
          <Field label="Why is this needed?" style={{ marginTop: '0.75rem' }}><input style={{ ...S.inp, width: '100%' }} value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} /></Field>
          <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '0.75rem', marginTop: '0.9rem' }}>
            <strong style={{ fontVariantNumeric: 'tabular-nums' }}>Estimated {fmtMoney(total)}</strong>
            <button type="button" className="btn-muted" onClick={() => setForm(null)} disabled={busy}>Cancel</button>
            <button type="button" className="btn-primary" onClick={submit} disabled={busy}>{busy ? 'Sending…' : 'Send for approval'}</button>
          </div>
        </div>
      )}
      <div style={S.tableWrap}>
        <table style={S.table}>
          <thead><tr>
            <th style={S.th}>PR no.</th><th style={S.th}>Date</th><th style={S.th}>Store</th><th style={S.th}>Requested by</th><th style={S.th}>Needed by</th>
            <th style={{ ...S.th, ...S.num }}>Lines</th><th style={{ ...S.th, ...S.num }}>Estimated</th><th style={S.th}>Status</th><th style={S.th} />
          </tr></thead>
          <tbody>
            {rows.map((d) => (
              <tr key={d.id}>
                <td style={{ ...S.td, fontWeight: 600, whiteSpace: 'nowrap' }}>{d.doc_no}</td><td style={S.td}>{fmtDate(d.doc_date)}</td>
                <td style={S.td}>{d.to_store_name}</td><td style={S.td}>{d.party_name || d.created_by}</td>
                <td style={S.td}>{d.expected_date ? fmtDate(d.expected_date) : '—'}</td>
                <td style={{ ...S.td, ...S.num }}>{d.line_count}</td><td style={{ ...S.td, ...S.num }}>{fmtMoney(d.grand_total)}</td>
                <td style={S.td}>{statusPill(d.status)}</td>
                <td style={S.td}><button type="button" className="btn-muted btn-sm" onClick={() => open(d.id)}>View</button></td>
              </tr>
            ))}
            {!rows.length && <tr><td colSpan={9} style={S.empty}>{loading ? 'Loading…' : 'No purchase requests.'}</td></tr>}
          </tbody>
        </table>
      </div>
      {view && (
        <DocView doc={view} onClose={() => setView(null)}>
          {err && <div style={{ ...S.err, flexBasis: '100%' }}>{err}</div>}
          {['SUBMITTED', 'APPROVED'].includes(view.status) && (
            <button type="button" className="btn-muted" disabled={busy} onClick={() => {
              const reason = window.prompt('Reason for rejecting / cancelling this request?');
              if (reason) act(`/pr/${view.id}/cancel`, { reason }, (r) => `${r.doc_no} ${String(r.status).toLowerCase()}.`);
            }}>Reject / cancel</button>
          )}
          {view.status === 'SUBMITTED' && view.approval?.can_approve && (
            <button type="button" className="btn-primary" disabled={busy} onClick={() => act(`/pr/${view.id}/approve`, {}, (r) => (r.status === 'APPROVED' ? `${r.doc_no} approved.` : `${r.doc_no}: level approved, waiting for the next level.`))}>Approve level {view.approval.next.level}</button>
          )}
          {view.status === 'APPROVED' && canOrder && (() => {
            const vendorOf = (l) => lineVendors[l.id] || '';
            const missing = view.lines.filter((l) => !vendorOf(l)).length;
            const poCount = new Set(view.lines.map(vendorOf).filter(Boolean)).size;
            const vendorSelect = (value, onChange, label) => (
              <Select style={{ ...S.inp, minWidth: 220 }} value={value} onChange={(e) => onChange(e.target.value)}>
                <option value="">{label}</option>
                {vendors.map((v) => <option key={v.id} value={v.id}>{v.vendor_name}</option>)}
              </Select>
            );
            return (
              <div style={{ flexBasis: '100%', borderTop: '1px solid #e2e8f0', paddingTop: '0.75rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap', marginBottom: '0.6rem' }}>
                  <strong style={{ fontSize: '0.9rem', color: '#1e3a5f' }}>Order from</strong>
                  {vendorSelect('', (id) => id && setLineVendors(Object.fromEntries(view.lines.map((l) => [l.id, id]))), 'Same vendor for all lines…')}
                  <span style={small}>or pick a vendor per line. One purchase order is made per vendor.</span>
                </div>
                <div style={S.tableWrap}>
                  <table style={S.table}>
                    <thead><tr><th style={S.th}>Item</th><th style={{ ...S.th, ...S.num }}>Qty</th><th style={S.th}>Vendor</th></tr></thead>
                    <tbody>
                      {view.lines.map((l) => (
                        <tr key={l.id}>
                          <td style={S.td}>{l.item_name} <span style={small}>({l.item_code})</span></td>
                          <td style={{ ...S.td, ...S.num }}>{fmtQty(l.qty)} {l.uom_code}</td>
                          <td style={S.td}>{vendorSelect(vendorOf(l), (id) => setLineVendors((m) => ({ ...m, [l.id]: id })), 'Choose vendor…')}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '0.6rem' }}>
                  <button type="button" className="btn-primary" disabled={busy || missing > 0}
                    title={missing ? `Choose a vendor for ${missing} line(s)` : ''}
                    onClick={() => act(`/pr/${view.id}/to-po`, { lines: view.lines.map((l) => ({ line_id: l.id, vendor_id: vendorOf(l) })) },
                      (r) => `${r.pos.length > 1 ? `Purchase orders ${r.doc_no}` : `Purchase order ${r.doc_no}`} created from ${view.doc_no} — review and approve under Purchase Orders.`)}>
                    {poCount > 1 ? `Create ${poCount} POs` : 'Create PO'}
                  </button>
                </div>
              </div>
            );
          })()}
        </DocView>
      )}
    </>
  );
}

// ── Approvals inbox ──────────────────────────────────────────────────────
export function Approvals({ api }) {
  const { rows, err: listErr, loading, load } = useList(api, '/approvals/pending');
  const [view, setView] = useState(null);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);
  const path = (d, a) => `/${d.doc_type === 'PR' ? 'pr' : 'po'}/${d.id}/${a}`;
  const act = async (d, action) => {
    let body = {};
    if (action === 'cancel') {
      const reason = window.prompt(`Reason for rejecting ${d.doc_no}?`);
      if (!reason) return;
      body = { reason };
    }
    setBusy(true); setErr('');
    try {
      const r = await api(path(d, action), { method: 'POST', body });
      setMsg(action === 'cancel' ? `${d.doc_no} rejected.` : r.status === 'APPROVED' ? `${d.doc_no} approved.` : `${d.doc_no}: your level approved; it moves to the next approver.`);
      setView(null); load();
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };
  return (
    <>
      {(err || listErr) && !view && <div style={S.err}>{err || listErr}</div>}
      {msg && <div style={S.ok}>{msg}</div>}
      <div style={S.tableWrap}>
        <table style={S.table}>
          <thead><tr>
            <th style={S.th}>Document</th><th style={S.th}>Date</th><th style={S.th}>Store / vendor</th><th style={{ ...S.th, ...S.num }}>Amount</th>
            <th style={S.th}>Your level</th><th style={S.th}>Raised by</th><th style={S.th} />
          </tr></thead>
          <tbody>
            {rows.map((d) => (
              <tr key={d.id}>
                <td style={S.td}><strong>{d.doc_no}</strong><div style={small}>{d.doc_type === 'PR' ? 'Purchase request' : 'Purchase order'}</div></td>
                <td style={S.td}>{fmtDate(d.doc_date)}</td>
                <td style={S.td}>{d.to_store_name}{d.vendor_name ? <div style={small}>{d.vendor_name}</div> : null}</td>
                <td style={{ ...S.td, ...S.num, fontWeight: 700 }}>{fmtMoney(d.grand_total)}</td>
                <td style={S.td}>Level {d.approval.next?.level} of {d.approval.levels.length}</td>
                <td style={S.td}>{d.party_name || d.created_by}</td>
                <td style={{ ...S.td, whiteSpace: 'nowrap' }}>
                  <button type="button" className="btn-muted btn-sm" onClick={() => openWithApproval(api, d.id).then(setView).catch((e) => setErr(e.message))}>View</button>{' '}
                  <button type="button" className="btn-muted btn-sm" disabled={busy} onClick={() => act(d, 'cancel')}>Reject</button>{' '}
                  <button type="button" className="btn-primary btn-sm" disabled={busy} onClick={() => act(d, 'approve')}>Approve</button>
                </td>
              </tr>
            ))}
            {!rows.length && <tr><td colSpan={7} style={S.empty}>{loading ? 'Loading…' : 'Nothing is waiting for your approval.'}</td></tr>}
          </tbody>
        </table>
      </div>
      {view && (
        <DocView doc={view} onClose={() => setView(null)}>
          {err && <div style={{ ...S.err, flexBasis: '100%' }}>{err}</div>}
          {view.approval?.can_approve && (
            <>
              <button type="button" className="btn-muted" disabled={busy} onClick={() => act(view, 'cancel')}>Reject</button>
              <button type="button" className="btn-primary" disabled={busy} onClick={() => act(view, 'approve')}>Approve level {view.approval.next.level}</button>
            </>
          )}
        </DocView>
      )}
    </>
  );
}

// ── Approval rules (Settings tab, Hotel Admin) ───────────────────────────
export function ApprovalRules({ api, boot }) {
  const [rules, setRules] = useState(null);
  const [roles, setRoles] = useState([]);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api('/approval-rules').then((r) => {
      setRoles(r.roles);
      setRules(r.rules.map((x) => ({ key: key(), doc_type: x.doc_type, department_id: x.department_id || '', min_amount: String(n(x.min_amount)), max_amount: x.max_amount == null ? '' : String(n(x.max_amount)), level: String(x.level), approver_role: x.approver_role, is_active: x.is_active })));
    }).catch((e) => setErr(e.message));
  }, [api]);

  const setRule = (k, patch) => setRules((rs) => rs.map((r) => (r.key === k ? { ...r, ...patch } : r)));
  const save = async () => {
    setSaving(true); setErr(''); setMsg('');
    try { const r = await api('/approval-rules', { method: 'PUT', body: { rules } }); setMsg(`${r.saved} rule(s) saved.`); } catch (e) { setErr(e.message); } finally { setSaving(false); }
  };
  if (!rules) return err ? <div style={S.err}>{err}</div> : <div style={S.empty}>Loading…</div>;

  return (
    <div style={S.card}>
      <h4 style={{ margin: '0 0 0.4rem' }}>Approval rules</h4>
      <p style={{ ...S.sub, marginTop: 0 }}>
        Each rule adds an approval level for documents whose amount falls in its range. Levels are approved in order; two roles on the same level means either can approve.
        With no rule, a purchase request needs a Department Head or Purchase Manager, and a purchase order needs a Purchase Manager. Hotel Admin can approve any level.
      </p>
      {err && <div style={S.err}>{err}</div>}
      {msg && <div style={S.ok}>{msg}</div>}
      <div style={S.tableWrap}>
        <table style={S.table}>
          <thead><tr>
            <th style={S.th}>Document</th><th style={S.th}>Department</th><th style={{ ...S.th, ...S.num }}>Amount from</th><th style={{ ...S.th, ...S.num }}>Up to</th>
            <th style={S.th}>Level</th><th style={S.th}>Approved by</th><th style={S.th}>Active</th><th style={S.th} />
          </tr></thead>
          <tbody>
            {rules.map((r) => (
              <tr key={r.key}>
                <td style={S.td}><Select style={S.inp} value={r.doc_type} onChange={(e) => setRule(r.key, { doc_type: e.target.value })}><option value="PR">Purchase request</option><option value="PO">Purchase order</option></Select></td>
                <td style={S.td}>
                  <Select style={S.inp} value={r.department_id} onChange={(e) => setRule(r.key, { department_id: e.target.value })}>
                    <option value="">All departments</option>
                    {boot.departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                  </Select>
                </td>
                <td style={S.td}><input type="number" min="0" step="any" style={{ ...S.inp, width: 110, textAlign: 'right' }} value={r.min_amount} onChange={(e) => setRule(r.key, { min_amount: e.target.value })} /></td>
                <td style={S.td}><input type="number" min="0" step="any" style={{ ...S.inp, width: 110, textAlign: 'right' }} value={r.max_amount} placeholder="no limit" onChange={(e) => setRule(r.key, { max_amount: e.target.value })} /></td>
                <td style={S.td}><Select style={S.inp} value={r.level} onChange={(e) => setRule(r.key, { level: e.target.value })}>{[1, 2, 3, 4, 5].map((x) => <option key={x} value={String(x)}>{x}</option>)}</Select></td>
                <td style={S.td}><Select style={S.inp} value={r.approver_role} onChange={(e) => setRule(r.key, { approver_role: e.target.value })}>{roles.map((x) => <option key={x} value={x}>{roleText(x)}</option>)}</Select></td>
                <td style={S.td}><input type="checkbox" checked={r.is_active} onChange={(e) => setRule(r.key, { is_active: e.target.checked })} aria-label="Active" /></td>
                <td style={S.td}><button type="button" className="btn-muted btn-sm" aria-label="Remove rule" onClick={() => setRules((rs) => rs.filter((x) => x.key !== r.key))}>✕</button></td>
              </tr>
            ))}
            {!rules.length && <tr><td colSpan={8} style={S.empty}>No rules — the defaults above apply.</td></tr>}
          </tbody>
        </table>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.5rem', marginTop: '0.75rem', flexWrap: 'wrap' }}>
        <button type="button" className="btn-muted btn-sm" onClick={() => setRules((rs) => [...rs, { key: key(), doc_type: 'PO', department_id: '', min_amount: '0', max_amount: '', level: '1', approver_role: 'PURCHASE_MANAGER', is_active: true }])}>+ Add rule</button>
        <button type="button" className="btn-primary" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save rules'}</button>
      </div>
      <p style={{ ...S.sub, marginTop: '0.75rem' }}>Example: PO up to ₹25,000 → level 1 Purchase Manager. PO above ₹25,000 → level 1 Purchase Manager and level 2 Hotel Admin.</p>
    </div>
  );
}
